import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useEditingEnvMap } from '../../api/editingEnvMapApi';
import { useEnv } from '../../context/EnvContext';
import EnvBadge from '../Layout/EnvBadge';
import EnvPill from '../../components/ui/EnvPill';
import { colors, typo } from '../../theme/tokens';

/** Compact ops stat tile for the map summary strip. */
const Stat: React.FC<{ label: React.ReactNode; value: React.ReactNode; warn?: boolean }> = ({
  label,
  value,
  warn = false,
}) => (
  <Box
    sx={{
      px: 1.75,
      py: 1,
      borderRadius: '12px',
      minWidth: 96,
      backgroundColor: warn ? '#FFFCF4' : colors.bgWhite,
      border: `1px solid ${warn ? colors.warningBorder : colors.borderLight}`,
    }}
  >
    <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', color: warn ? colors.warningText : colors.textPrimary, lineHeight: 1.1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: warn ? colors.warningText : colors.textSecondary }}>
      {label}
    </Typography>
  </Box>
);

/**
 * WAVE G — Editing-Environment Map.
 *
 * Read-only ops table: every tenant's authoring home env, whether it's the
 * implicit default, current source-env version, and a drift flag. Backed by
 * GET /api/admin/editing-env-map (promotion.editing_env_overview view).
 *
 * Degrades gracefully: when the backend reports `available: false` (view not
 * present / feature not yet migrated) or the route 404s, we render an info
 * notice instead of an empty/broken table.
 */
const EditingEnvMap: React.FC = () => {
  const { data, isLoading, isError } = useEditingEnvMap();
  // /api/admin/editing-env-map is home-only (axiosClient never proxies it to a
  // remote env). So data.currentEnv reflects the HOME env regardless of which
  // env the user is currently browsing. We show a note when the user has
  // selected a different browse env so the display is not confusing (#21).
  const { currentEnv: browseEnv, editableEnv } = useEnv();
  const isRemoteBrowse = browseEnv !== editableEnv;

  return (
    <Box sx={{ p: 1 }}>
      <Typography sx={{ ...typo.sectionTitle, mb: 0.5 }}>Editing-Environment Map</Typography>
      <Typography sx={{ ...typo.bodyText, color: colors.textSecondary, mb: 2 }}>
        Each tenant&apos;s authoring home — the environment it is edited and promoted from.
        {' '}This data is always read from the <strong>{editableEnv}</strong> (home) backend — it is the
        authoritative source regardless of which env you are browsing.
        {isRemoteBrowse && (
          <>
            {' '}You are currently browsing <strong>{browseEnv}</strong>.
          </>
        )}
      </Typography>

      {data.available && data.rows.length > 0 && (() => {
        const byEnv: Record<string, number> = {};
        for (const r of data.rows) byEnv[r.homeEnv] = (byEnv[r.homeEnv] ?? 0) + 1;
        const migrating = data.rows.filter((r) => r.migratingTo).length;
        const drift = data.rows.filter((r) => r.drift).length;
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }} data-testid="editing-env-map-summary">
            <Stat label="Tenants" value={data.rows.length} />
            {['dev01', 'qa', 'uat', 'integ'].filter((e) => byEnv[e]).map((e) => (
              <Box
                key={e}
                sx={{
                  px: 1.75,
                  py: 1,
                  borderRadius: '12px',
                  backgroundColor: colors.bgWhite,
                  border: `1px solid ${colors.borderLight}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  justifyContent: 'center',
                  minWidth: 78,
                }}
              >
                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1 }}>
                  {byEnv[e]}
                </Typography>
                <EnvPill env={e} size="sm" />
              </Box>
            ))}
            {migrating > 0 && <Stat label="Migrating" value={migrating} />}
            <Stat label="Drift" value={drift} warn={drift > 0} />
          </Box>
        );
      })()}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      ) : isError ? (
        <Alert severity="error" data-testid="editing-env-map-error">
          Failed to load the editing-environment map.
        </Alert>
      ) : !data.available ? (
        <Alert severity="info" data-testid="editing-env-map-unavailable">
          The per-tenant authoring-environment map is not available on this deployment yet.
        </Alert>
      ) : data.rows.length === 0 ? (
        <Alert severity="info" data-testid="editing-env-map-empty">
          No tenants found.
        </Alert>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: '12px' }}
          data-testid="editing-env-map-table"
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...typo.sectionLabel }}>Tenant</TableCell>
                <TableCell sx={{ ...typo.sectionLabel }}>Home env</TableCell>
                <TableCell sx={{ ...typo.sectionLabel }}>Version</TableCell>
                <TableCell sx={{ ...typo.sectionLabel }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow
                  key={r.tenantCode}
                  hover
                  data-testid={`editing-env-map-row-${r.tenantCode}`}
                  sx={r.drift ? { backgroundColor: '#FFFCF4' } : undefined}
                >
                  <TableCell>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {r.tenantName || r.tenantCode}
                    </Typography>
                    <Typography sx={{ ...typo.tinyText, color: colors.textTertiary }}>{r.tenantCode}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EnvBadge env={r.homeEnv} />
                      {r.isDefaultHome && (
                        <Tooltip title="No explicit home set — defaults to dev01.">
                          <Chip
                            size="small"
                            label="default"
                            sx={{ height: 18, fontSize: 10, bgcolor: colors.bgMuted, color: colors.textTertiary }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 13 }}>
                      {r.currentVersion ?? <span style={{ color: colors.textTertiary }}>—</span>}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {r.migratingTo && (
                        <Chip
                          size="small"
                          label={`migrating → ${r.migratingTo}`}
                          sx={{ height: 20, fontSize: 11, bgcolor: colors.infoBgStrong, color: colors.textPrimary }}
                        />
                      )}
                      {r.drift && (
                        <Tooltip title="The home env disagrees with where versions live, or a handoff is mid-flight.">
                          <Chip
                            size="small"
                            icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                            label="drift"
                            data-testid={`editing-env-map-drift-${r.tenantCode}`}
                            sx={{ height: 20, fontSize: 11, bgcolor: colors.warningBgStrong, color: colors.warningText }}
                          />
                        </Tooltip>
                      )}
                      {!r.migratingTo && !r.drift && (
                        <Typography sx={{ ...typo.tinyText, color: colors.textTertiary }}>ok</Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default EditingEnvMap;
